/**
 * FacilityHero — "the facility at a glance" band (spec D1/D2,
 * 2026-07-09 admin dashboard wave; charter #221; Ted #18 → #922).
 *
 * Ted's 6:45am question, answered in one glance: who's here, who's coming,
 * who's leaving, who sleeps here tonight, am I staffed, anything to chase?
 *
 * Ted #18 (texts 2026-09-09): *"a live snapshot such as: ON SITE NOW: 27 |
 * ARRIVING: 8 | GOING HOME: 12 | OVERNIGHT TONIGHT: 11 … an immediate
 * operational picture of the facility without having to calculate
 * anything."* That line is now the hero's first band.
 *
 * Composition:
 *  - rollup (page-owned `/rollup`, passed down — the SAME payload the
 *    DailyRollup table and the day list read, Principle 1) → the four
 *    snapshot numbers, each a Link into `/calendar/day` pre-filtered to
 *    exactly the dogs it counted; the Ted A6 total-on-site meter
 *    (`facility`, provenance-labelled, amber ≥ 90 %, red at the limit —
 *    a WARNING colour, never a gate); the "N of M arrived" caption; a
 *    quiet "N unconfirmed →" chase line when there is anything to chase.
 *  - Sawyer, 10-30: "Check in arriving dogs ↓", the one-tap way from the
 *    ARRIVING number to the list under this hero that holds the Check In
 *    buttons. Ted's numbers stay links into the day list; the jump sits beside
 *    them, on the first screen, only while somebody is still to arrive.
 *  - facility-status → 3 per-service occupancy meters with REAL
 *    denominators (Lane A2 `services`); tapping a meter opens the
 *    preserved FacilityDrilldown. Its blended `onSite` is no longer shown:
 *    it sums booking-dogs (a dog with daycare AND grooming counts twice,
 *    which is right for the per-pool meters) while Ted's number is
 *    distinct dogs — two definitions of "on site" on one hero was exactly
 *    the disagreement Principle 1 forbids.
 *  - staff-on-duty → staffing tile (unchanged; #427 W1 both-truths).
 *  - compliance-alerts → alert strip ("Bella — rabies expired").
 *
 * Retired by #922 (superseded, never restated): the #563 StatusCounts strip
 * (its Expected / Going home chips counted BOOKINGS above a hero that now
 * counts dogs; its going-home-by-service breakdown is the DailyRollup's
 * rows; its Needs-attention count is the alert strip, which names the
 * dogs) and the hero's arrivals / departures tiles (ARRIVING and GOING
 * HOME are the same facts in Ted's words).
 *
 * Data honesty (Principle 5): when the page-level probe says today's zeros
 * are structural (`scheduleSuspect`), every snapshot number renders "—"
 * with a stale caption, never a confident 0; the sync chip shows the
 * mirror age whenever a Gingr mirror exists.
 *
 * Every sub-surface ships its five states: skeleton (loading), empty
 * (true zeros, honestly 0), error (recovery), stale (honest copy /
 * chip), default.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  capacityTone,
  fetchComplianceAlerts,
  fetchFacilityStatus,
  fetchStaffOnDuty,
  type ComplianceAlert,
  type DashboardRollupResponse,
  type DashboardServiceKey,
  type DashboardDataStatus,
  type DaySnapshot,
  type FacilityMeter,
  type FacilityStatusResponse,
  type ServiceOccupancy,
  type StaffingNeed,
} from './dashboard-api.js';
import {
  countDogs,
  dayListHref,
  filterDayItems,
  serviceMeterFacts,
  type DayView,
  type ServiceMeterFacts,
} from './day-list-filters.js';
import { FacilityDrilldown } from './FacilityDrilldown.js';
import { useDashboardResource } from './useDashboardResource.js';

const SERVICE_KEYS: ReadonlyArray<DashboardServiceKey> = [
  'daycare',
  'boarding',
  'grooming',
];

/** How many alert rows render before collapsing into "+N more". */
const ALERT_PREVIEW_LIMIT = 4;

/**
 * Ted's four numbers, in his order. `view` is the day-list filter the tap
 * opens — the client mirror of the server fold (day-list-filters.ts), so
 * "ARRIVING 8" opens exactly those 8 dogs.
 */
const SNAPSHOT_ITEMS: ReadonlyArray<{
  key: keyof Pick<DaySnapshot, 'onSiteNow' | 'arriving' | 'goingHome' | 'overnightTonight'>;
  view: DayView;
  testId: string;
}> = [
  { key: 'onSiteNow', view: 'onSite', testId: 'on-site' },
  { key: 'arriving', view: 'arriving', testId: 'arriving' },
  { key: 'goingHome', view: 'goingHome', testId: 'going-home' },
  { key: 'overnightTonight', view: 'overnight', testId: 'overnight' },
];

export interface FacilityHeroProps {
  apiUrl: string;
  token: string;
  date: string;
  /** Bump to refetch (shell Quick Book signal + check-in mutations). */
  refreshKey?: number;
  /** Page-level data-status probe result; null while loading/failed. */
  dataStatus: DashboardDataStatus | null;
  /** True when today's zeros are structural (isScheduleDataSuspect). */
  scheduleSuspect: boolean;
  /**
   * Wave 5 (#922) — the page-owned `/rollup` (one source with the
   * DailyRollup table and the day list, so the strip can never disagree
   * with the table beneath it). Null while loading / failed / not fetched.
   */
  rollup: DashboardRollupResponse | null;
  rollupLoading: boolean;
  rollupError: string | null;
  onRetryRollup: () => void;
  /** Sawyer 10-30 — scroll to the arrivals list. Absent ⇒ no jump is offered. */
  onCheckInJump?: () => void;
}

export function FacilityHero({
  apiUrl,
  token,
  date,
  refreshKey = 0,
  dataStatus,
  scheduleSuspect,
  rollup,
  rollupLoading,
  rollupError,
  onRetryRollup,
  onCheckInJump,
}: FacilityHeroProps): JSX.Element {
  const { t } = useTranslation();
  const [drilldown, setDrilldown] = useState<DashboardServiceKey | null>(null);

  const facility = useDashboardResource<FacilityStatusResponse>(
    () => fetchFacilityStatus(apiUrl, token, date),
    refreshKey,
  );
  const staffing = useDashboardResource(
    () => fetchStaffOnDuty(apiUrl, token, date),
    refreshKey,
  );
  const alerts = useDashboardResource(
    () => fetchComplianceAlerts(apiUrl, token, date),
    refreshKey,
  );

  const serviceLabels: Record<DashboardServiceKey, string> = {
    daycare: t('admin.dashboard.service.daycare'),
    boarding: t('admin.dashboard.service.boarding'),
    grooming: t('admin.dashboard.service.grooming'),
  };

  return (
    <section
      className="ferro-hero"
      aria-label={t('admin.dashboard.hero.label')}
      data-testid="facility-hero"
    >
      {/* ── Ted #18: the live snapshot (the hero's first band) ─────── */}
      <SnapshotStrip
        date={date}
        rollup={rollup}
        loading={rollupLoading}
        error={rollupError}
        onRetry={onRetryRollup}
        suspect={scheduleSuspect}
        dataStatus={dataStatus}
        onCheckInJump={onCheckInJump}
      />

      <div className="ferro-hero__primary">
        {/* ── Per-service occupancy meters (real denominators) ──── */}
        <div
          className="ferro-hero__meters"
          role="group"
          aria-label={t('admin.dashboard.hero.metersLabel')}
        >
          {facility.isLoading ? (
            <>
              <div className="ferro-hero__skeleton ferro-hero__skeleton--meter" aria-hidden="true" />
              <div className="ferro-hero__skeleton ferro-hero__skeleton--meter" aria-hidden="true" />
              <div className="ferro-hero__skeleton ferro-hero__skeleton--meter" aria-hidden="true" />
            </>
          ) : facility.error && !facility.data ? (
            <div className="ferro-hero__error" role="alert">
              <p>{t('admin.dashboard.hero.facilityError')}</p>
              <button
                type="button"
                className="ferro-btn ferro-btn--ghost"
                onClick={facility.retry}
              >
                {t('admin.dashboard.hero.retry')}
              </button>
            </div>
          ) : (
            facility.data &&
            SERVICE_KEYS.map((service) => (
              <ServiceMeter
                key={service}
                service={service}
                label={serviceLabels[service]}
                occupancy={
                  facility.data?.services?.[service] ?? {
                    onSite: facility.data?.serviceBreakdown[service] ?? 0,
                    capacity: null,
                    capacitySource: 'default',
                  }
                }
                facts={
                  // Principle 5 — suspect zeros say nothing rather than
                  // "None booked today"; no fold yet (loading, failed, or an
                  // older server) ⇒ the meter reads exactly as it did.
                  !scheduleSuspect && Array.isArray(rollup?.items)
                    ? serviceMeterFacts(rollup.items, service)
                    : null
                }
                onOpen={() => setDrilldown(service)}
              />
            ))
          )}
        </div>

        {/* Staffing tile — BOTH truths (#427 W1d): "on site now" = open
            punches (the endpoint's additive onSite block), "scheduled
            today" = StaffSchedule intent. The schedules-teaching state
            is untouched. When there are no open punches the client
            cannot tell "everyone's out" from "the time clock isn't in
            use" (no kiosk info on this read), so it renders a quiet "—"
            with an INLINE caption — never a confident zero and never a
            tooltip-only explanation (P5). New copy is literal English
            pending the W1 i18n key block. */}
        <HeroTile
          testId="hero-staffing"
          title={t('admin.dashboard.hero.staffingTitle')}
          loading={dataStatus?.staffSchedulesLoaded === false ? false : staffing.isLoading}
          error={
            staffing.error && !staffing.data
              ? t('admin.dashboard.hero.staffingError')
              : null
          }
          onRetry={staffing.retry}
        >
          {dataStatus &&
          dataStatus.staffSchedulesLoaded === false &&
          !(staffing.data?.onSite && staffing.data.onSite.count > 0) ? (
            // Schedules never entered AND no open punches → pure teaching
            // state. But real punches OUTRANK the teaching state (spec
            // W1d reconciliation): a clocked-in staffer must never be
            // invisible because scheduling isn't in use yet — the two
            // truths are independent (P5).
            <div
              className="ferro-hero__teach"
              data-testid="hero-staffing-empty"
            >
              <p className="ferro-hero__teach-copy">
                {t('admin.dashboard.hero.shiftsNotTracked')}
              </p>
              <Link to="/schedule" className="ferro-hero__teach-link">
                {t('admin.dashboard.hero.setUpSchedules')} →
              </Link>
            </div>
          ) : (
            <>
              {staffing.data?.onSite && staffing.data.onSite.count > 0 ? (
                <span
                  className="ferro-hero__tile-stat"
                  data-testid="hero-staffing-onsite"
                >
                  {staffing.data.onSite.count}
                </span>
              ) : (
                <span
                  className="ferro-hero__tile-stat ferro-hero__tile-stat--unknown"
                  data-testid="hero-staffing-onsite"
                  aria-label="On-site staff count unknown — no open punches recorded"
                >
                  —
                </span>
              )}
              <span className="ferro-hero__tile-label">on site now</span>
              {/* Ted A5 (#933 PR 4) — the owner's supervision ratio, met or
                  not, from the SAME rollup read the tiles above use (no
                  second fetch, #922's rule). A warning, never a gate: the
                  booking engine is what refuses a dog. Absent `tone` means
                  an older server — render nothing rather than a false
                  reassurance (Principle 5). */}
              <StaffingRatioNote
                staffing={
                  rollup !== null && typeof rollup.staffing === 'object' && rollup.staffing !== null
                    ? rollup.staffing
                    : null
                }
              />
              {dataStatus && dataStatus.staffSchedulesLoaded === false ? (
                // Punches exist but scheduling was never set up: an
                // honest caption instead of a fake "0 scheduled" (P5).
                <span
                  className="ferro-hero__tile-caption"
                  data-testid="hero-staffing-scheduled"
                >
                  <Link to="/schedule" className="ferro-hero__teach-link">
                    {t('admin.dashboard.hero.setUpSchedules')} →
                  </Link>
                  {staffing.data?.onSite && staffing.data.onSite.onBreak > 0
                    ? ` · ${staffing.data.onSite.onBreak} on break`
                    : ''}
                </span>
              ) : (
                <span
                  className="ferro-hero__tile-caption"
                  data-testid="hero-staffing-scheduled"
                >
                  <span data-testid="hero-staffing-count">
                    {staffing.data?.totalOnShift ?? 0}
                  </span>{' '}
                  scheduled today
                  {staffing.data?.onSite && staffing.data.onSite.onBreak > 0
                    ? ` · ${staffing.data.onSite.onBreak} on break`
                    : ''}
                </span>
              )}
              {staffing.data?.onSite && staffing.data.onSite.count > 0 ? (
                staffing.data.totalOnShift > 0 ? (
                  <span className="ferro-hero__tile-caption">
                    {t('admin.dashboard.hero.staffRatio', {
                      ratio: staffing.data.ratio,
                    })}
                  </span>
                ) : null
              ) : (
                <span className="ferro-hero__tile-caption">
                  No open punches — the time clock may not be in use yet.
                </span>
              )}
              <Link to="/schedule" className="ferro-hero__tile-link">
                {t('admin.dashboard.hero.viewSchedule')} →
              </Link>
            </>
          )}
        </HeroTile>
      </div>

      {/* ── Today-scoped alert strip (compliance-alerts) ─────────── */}
      <AlertStrip
        loading={alerts.isLoading}
        error={alerts.error && !alerts.data ? alerts.error : null}
        onRetry={alerts.retry}
        items={alerts.data?.alerts ?? []}
      />

      {drilldown && (
        <FacilityDrilldown
          apiUrl={apiUrl}
          token={token}
          date={date}
          service={drilldown}
          onClose={() => setDrilldown(null)}
        />
      )}
    </section>
  );
}

/**
 * Ted #18 — ON SITE NOW · ARRIVING · GOING HOME · OVERNIGHT TONIGHT, each a
 * tap into the day list, plus the Ted A6 floor meter. Reads ONLY the
 * page-owned rollup: the numbers here are the server's fold over the same
 * rows the DailyRollup and the day list render, so a tapped number opens
 * exactly the dogs it counted and nothing is recounted on the client.
 */
function SnapshotStrip({
  date,
  rollup,
  loading,
  error,
  onRetry,
  suspect,
  dataStatus,
  onCheckInJump,
}: {
  date: string;
  rollup: DashboardRollupResponse | null;
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  suspect: boolean;
  dataStatus: DashboardDataStatus | null;
  onCheckInJump?: () => void;
}): JSX.Element {
  const { t } = useTranslation();

  // A payload missing its fold (an older server answering 200 with a shape
  // this band does not know) is treated exactly like a failed read: the
  // honest error state, never a crash that takes the whole dashboard down.
  const usable =
    rollup !== null &&
    typeof rollup.snapshot === 'object' &&
    rollup.snapshot !== null &&
    typeof rollup.facility === 'object' &&
    rollup.facility !== null &&
    typeof rollup.totals === 'object' &&
    rollup.totals !== null &&
    Array.isArray(rollup.items);

  // Error (what happened + how to recover — Principle 2). Until the server
  // carries `/rollup`, this is what the hero's first band shows: honest,
  // with a Retry, never a made-up number.
  if (!usable && (error || rollup !== null)) {
    return (
      <div
        className="ferro-hero__error ferro-hero__error--snapshot"
        role="alert"
        data-testid="hero-snapshot-error"
      >
        <p>{t('admin.dashboard.hero.snapshotError')}</p>
        <button type="button" className="ferro-btn ferro-btn--ghost" onClick={onRetry}>
          {t('admin.dashboard.hero.retry')}
        </button>
      </div>
    );
  }

  // Loading — or not fetched yet (session still validating): skeleton
  // numbers, never a blank band (Principle 4).
  if (!usable || rollup === null) {
    return (
      <div
        className="ferro-hero__snapshot ferro-hero__snapshot--loading"
        data-testid="hero-snapshot-skeleton"
        aria-hidden="true"
        aria-busy={loading || undefined}
      >
        {SNAPSHOT_ITEMS.map((item) => (
          <div key={item.key} className="ferro-hero__skeleton ferro-hero__skeleton--snap" />
        ))}
        <div className="ferro-hero__skeleton ferro-hero__skeleton--snap" />
      </div>
    );
  }

  const snap = rollup.snapshot;
  const { totals } = rollup;
  // #5 — a structural zero must never read as a confident zero.
  const display = (n: number): string => (suspect && n === 0 ? '—' : String(n));
  const labels: Record<(typeof SNAPSHOT_ITEMS)[number]['key'], string> = {
    onSiteNow: t('admin.dashboard.hero.onSiteNow'),
    arriving: t('admin.dashboard.hero.arriving'),
    goingHome: t('admin.dashboard.hero.goingHome'),
    overnightTonight: t('admin.dashboard.hero.overnightTonight'),
  };
  // The #563 chase, kept as a quiet second line rather than a fifth
  // headline number: the same client mirror of the fold the day list uses.
  const unconfirmed = countDogs(
    filterDayItems(rollup.items, { serviceTypeId: null, view: 'unconfirmed' }),
  );

  return (
    <div
      className={`ferro-hero__snapshot ferro-hero__snapshot--${rollup.facility.tone}${
        suspect ? ' ferro-hero__snapshot--stale' : ''
      }`}
      role="group"
      aria-label={t('admin.dashboard.hero.snapshotLabel')}
      data-testid="hero-snapshot"
      data-tone={rollup.facility.tone}
    >
      {SNAPSHOT_ITEMS.map((item) => {
        const value = display(snap[item.key]);
        return (
          <Link
            key={item.key}
            to={dayListHref({ date, view: item.view })}
            className="ferro-hero__snap"
            aria-label={`${value} ${labels[item.key].toLowerCase()}`}
            data-testid={`hero-snapshot-${item.testId}`}
          >
            <span className="ferro-hero__snap-label">{labels[item.key]}</span>
            <span
              className="ferro-hero__snap-value"
              data-testid={item.key === 'onSiteNow' ? 'hero-onsite' : `hero-snapshot-${item.testId}-value`}
            >
              {value}
            </span>
            {item.key === 'onSiteNow' ? <SyncChip dataStatus={dataStatus} /> : null}
            {item.key === 'arriving' && !suspect && totals.expected > 0 ? (
              <span className="ferro-hero__snap-caption" data-testid="hero-arrivals-progress">
                {t('admin.dashboard.hero.arrivedProgress', {
                  arrived: totals.checkIns,
                  expected: totals.expected,
                })}
              </span>
            ) : null}
          </Link>
        );
      })}

      <FloorMeter facility={rollup.facility} date={date} suspect={suspect} />

      {unconfirmed > 0 ? (
        <Link
          to={dayListHref({ date, view: 'unconfirmed' })}
          className="ferro-hero__snap-chase"
          data-testid="hero-unconfirmed"
        >
          {t('admin.dashboard.hero.unconfirmed', { count: unconfirmed })} →
        </Link>
      ) : null}

      {onCheckInJump && !suspect && snap.arriving > 0 ? (
        <button
          type="button"
          className="ferro-hero__snap-chase ferro-hero__snap-jump"
          onClick={onCheckInJump}
          data-testid="hero-check-in-jump"
        >
          {t('admin.dashboard.hero.checkInJump')} ↓
        </button>
      ) : null}

      {suspect ? (
        <p className="ferro-hero__snap-stale" data-testid="hero-snapshot-stale-caption">
          {t('admin.dashboard.hero.snapshotStale')}
        </p>
      ) : null}
    </div>
  );
}

/**
 * Ted A6 (#927) + the Lodging note: the total-on-site meter — what the day
 * is PLANNED to hold against the owner's limit (Settings → Operations). The
 * server decides the tone (amber ≥ 90 %, red at the limit); this only
 * paints it, as a WARNING on the header, never a gate. A defaulted limit
 * says so (P5). Tapping opens everything on the day.
 */
function FloorMeter({
  facility,
  date,
  suspect,
}: {
  facility: FacilityMeter;
  date: string;
  suspect: boolean;
}): JSX.Element {
  const { t } = useTranslation();
  const value = suspect && facility.onFloorPlannedToday === 0 ? '—' : String(facility.onFloorPlannedToday);
  const pct =
    facility.limit > 0
      ? Math.min(100, Math.round((facility.onFloorPlannedToday / facility.limit) * 100))
      : 0;
  const word =
    facility.tone === 'red'
      ? t('admin.dashboard.hero.atCapacity')
      : facility.tone === 'amber'
        ? t('admin.dashboard.hero.approachingCapacity')
        : null;
  const label = t('admin.dashboard.hero.onFloorToday');
  return (
    <Link
      to={dayListHref({ date })}
      className={`ferro-hero__floor ferro-hero__floor--${facility.tone}`}
      aria-label={`${label}: ${value} ${t('admin.dashboard.hero.ofLimit', { limit: facility.limit })}${word ? ` — ${word}` : ''}`}
      data-testid="hero-floor"
      data-tone={facility.tone}
    >
      <span className="ferro-hero__snap-label">{label}</span>
      <span className="ferro-hero__floor-value" data-testid="hero-floor-value">
        {value}
        <span className="ferro-hero__floor-denom">
          {' '}
          {t('admin.dashboard.hero.ofLimit', { limit: facility.limit })}
        </span>
      </span>
      <span className="ferro-hero__floor-track" aria-hidden="true">
        <span className="ferro-hero__floor-fill" style={{ width: `${pct}%` }} />
      </span>
      {word ? (
        <span className="ferro-hero__floor-word" data-testid="hero-floor-word">
          {word}
        </span>
      ) : null}
      {facility.limitSource === 'default' ? (
        <span className="ferro-hero__snap-caption" data-testid="hero-floor-default">
          {t('admin.dashboard.hero.defaultLimit')}
        </span>
      ) : null}
    </Link>
  );
}

/**
 * "Synced today / N days ago" chip (spec D2). Rendered whenever a Gingr
 * mirror exists (`gingrMirrorAgeDays != null`); silent otherwise — a
 * native-only tenant has no mirror to be honest about. Ages ≥2 days
 * wear the --ferro-stale family: staleness must LOOK different.
 */
function SyncChip({
  dataStatus,
}: {
  dataStatus: DashboardDataStatus | null;
}): JSX.Element | null {
  const { t } = useTranslation();
  if (!dataStatus || dataStatus.gingrMirrorAgeDays === null) return null;
  const age = dataStatus.gingrMirrorAgeDays;
  const isStale = age >= 2;
  return (
    <span
      className={`ferro-hero__sync-chip${
        isStale ? ' ferro-hero__sync-chip--stale' : ''
      }`}
      data-testid="hero-sync-chip"
    >
      {age === 0
        ? t('admin.dashboard.hero.syncedToday')
        : t('admin.dashboard.hero.syncedDaysAgo', { count: age })}
    </span>
  );
}

function ServiceMeter({
  service,
  label,
  occupancy,
  facts,
  onOpen,
}: {
  service: DashboardServiceKey;
  label: string;
  occupancy: ServiceOccupancy;
  /** null ⇒ no trustworthy fold to read (see the call site); no fact lines. */
  facts: ServiceMeterFacts | null;
  onOpen: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const { onSite, capacity, capacitySource } = occupancy;
  const hasRealCapacity = capacitySource === 'rules' && capacity !== null;
  const pct =
    hasRealCapacity && capacity > 0
      ? Math.round((onSite / capacity) * 100)
      : null;
  // capacity === 0 with dogs on site = over a closed day → red; a clean
  // closed day reads as "Closed today", not a fake 0/0 quiet meter.
  const tone =
    pct !== null
      ? capacityTone(pct)
      : hasRealCapacity && onSite > 0
        ? 'red'
        : null;

  return (
    <button
      type="button"
      className={`ferro-hero__meter${tone ? ` ferro-hero__meter--${tone}` : ''}`}
      onClick={onOpen}
      aria-label={t('admin.dashboard.hero.showDrilldown', { service: label })}
      data-testid={`hero-meter-${service}`}
    >
      <span className="ferro-hero__meter-label">{label}</span>
      <span className="ferro-hero__meter-value" data-testid={`hero-meter-${service}-value`}>
        {onSite}
        {hasRealCapacity ? (
          <span className="ferro-hero__meter-denom">/{capacity}</span>
        ) : null}
      </span>
      {pct !== null ? (
        <span className="ferro-hero__meter-track" aria-hidden="true">
          <span
            className="ferro-hero__meter-fill"
            style={{ width: `${Math.min(pct, 100)}%` }}
          />
        </span>
      ) : (
        <span className="ferro-hero__meter-note">
          {hasRealCapacity
            ? t('admin.dashboard.hero.closedToday')
            : t('admin.dashboard.hero.capacityNotSet')}
        </span>
      )}
      {facts ? <MeterFacts service={service} facts={facts} /> : null}
    </button>
  );
}

/**
 * The lines under a meter's number: what is still going to happen today for
 * that service, in the desk's words. Each service shows the facts that
 * change what the desk does next — daycare has no "staying tonight", a
 * boarder has no "gone home" worth a line — and zeros are left out, so a
 * card never reads as a column of "0 …". Plain spans, not links: the whole
 * card is already the button that opens the roster.
 */
const METER_FACT_LINES: Record<
  DashboardServiceKey,
  ReadonlyArray<{ fact: keyof ServiceMeterFacts; key: string }>
> = {
  daycare: [
    { fact: 'arriving', key: 'arriving' },
    { fact: 'checkedOut', key: 'goneHome' },
  ],
  boarding: [
    { fact: 'arriving', key: 'arriving' },
    { fact: 'goingHome', key: 'checkingOut' },
    { fact: 'overnight', key: 'tonight' },
  ],
  grooming: [
    { fact: 'arriving', key: 'toCome' },
    { fact: 'checkedOut', key: 'done' },
  ],
};

function MeterFacts({
  service,
  facts,
}: {
  service: DashboardServiceKey;
  facts: ServiceMeterFacts;
}): JSX.Element {
  const { t } = useTranslation();
  const lines = METER_FACT_LINES[service].filter(({ fact }) => facts[fact] > 0);
  return (
    <span className="ferro-hero__meter-facts" data-testid={`hero-meter-${service}-facts`}>
      {lines.length > 0 ? (
        lines.map(({ fact, key }) => (
          <span key={key} className="ferro-hero__meter-fact">
            {t(`admin.dashboard.hero.meterFact.${key}`, { count: facts[fact] })}
          </span>
        ))
      ) : (
        <span className="ferro-hero__meter-fact ferro-hero__meter-fact--quiet">
          {facts.total > 0
            ? t('admin.dashboard.hero.meterFact.allIn')
            : t('admin.dashboard.hero.meterFact.noneToday')}
        </span>
      )}
    </span>
  );
}

/**
 * Ted, 09-08 (decision A5): *"15 dogs per handler, daycare + overnight,
 * excluding grooming and hiking."* The rollup answers whether that is true
 * right now; this says so in one line, in the desk's words.
 *
 *   red   — fewer handlers on the floor than the owner's own rule requires
 *   amber — exactly at the ratio: the next dog needs another handler
 *   green — covered; the line stays quiet rather than congratulating anyone
 *
 * No ratio set, or an older server that does not send the coverage → no
 * line at all. The one thing this must never do is imply a supervision
 * standard the owner never chose.
 */
function StaffingRatioNote({ staffing }: { staffing: StaffingNeed | null }): JSX.Element | null {
  // A catch-all test stub that answers `{}` reaches here as null, and an
  // older server reaches it with no `tone` — both render nothing (#922's
  // crash class: never index into a payload you have not shape-checked).
  if (!staffing || staffing.tone === undefined || staffing.dogsPerHandler === null) return null;
  if (staffing.tone === 'green') return null;
  const on = staffing.handlersOnNow ?? 0;
  const needed = staffing.handlersNeededNow ?? 0;
  const short = staffing.handlersShort ?? 0;
  const dogs = staffing.onFloorNow;
  return (
    <span
      className={`ferro-hero__tile-warning ferro-hero__tile-warning--${staffing.tone}`}
      role="status"
      data-testid="hero-staffing-ratio"
      data-tone={staffing.tone}
    >
      {staffing.tone === 'red'
        ? `${dogs} ${dogs === 1 ? 'dog' : 'dogs'} on the floor ${dogs === 1 ? 'needs' : 'need'} ${needed} ${needed === 1 ? 'handler' : 'handlers'} at ${staffing.dogsPerHandler} per handler — ${short} short`
        : `At ${staffing.dogsPerHandler} dogs per handler with ${on} on the floor: the next dog needs another handler`}
    </span>
  );
}

function HeroTile({
  testId,
  title,
  loading,
  error,
  onRetry,
  children,
}: {
  testId: string;
  title: string;
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  children: React.ReactNode;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="ferro-hero__tile" data-testid={testId}>
      <span className="ferro-hero__tile-title">{title}</span>
      {loading ? (
        <div
          className="ferro-hero__skeleton ferro-hero__skeleton--tile"
          data-testid={`${testId}-skeleton`}
          aria-hidden="true"
        />
      ) : error ? (
        <div className="ferro-hero__error" role="alert">
          <p>{error}</p>
          <button
            type="button"
            className="ferro-btn ferro-btn--ghost"
            onClick={onRetry}
          >
            {t('admin.dashboard.hero.retry')}
          </button>
        </div>
      ) : (
        children
      )}
    </div>
  );
}

function AlertStrip({
  loading,
  error,
  onRetry,
  items,
}: {
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  items: ReadonlyArray<ComplianceAlert>;
}): JSX.Element {
  const { t } = useTranslation();
  const preview = items.slice(0, ALERT_PREVIEW_LIMIT);
  const overflow = items.length - preview.length;

  if (loading) {
    return (
      <div
        className="ferro-hero__skeleton ferro-hero__skeleton--strip"
        data-testid="hero-alerts-skeleton"
        aria-hidden="true"
      />
    );
  }

  if (error) {
    return (
      <div className="ferro-hero__alerts ferro-hero__alerts--error" role="alert">
        <span>{t('admin.dashboard.hero.alertsError')}</span>
        <button
          type="button"
          className="ferro-btn ferro-btn--ghost"
          onClick={onRetry}
        >
          {t('admin.dashboard.hero.retry')}
        </button>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <p
        className="ferro-hero__alerts ferro-hero__alerts--clear"
        data-testid="hero-alerts-clear"
      >
        {t('admin.dashboard.hero.alertsClear')}
      </p>
    );
  }

  return (
    <div
      className="ferro-hero__alerts ferro-hero__alerts--active"
      data-testid="hero-alerts"
      aria-label={t('admin.dashboard.hero.alertsTitle')}
    >
      {/* Plain links (no list roles): overriding an <a>'s role with
          listitem strips its link semantics from the a11y tree. */}
      {preview.map((alert) => (
        <Link
          key={`${alert.dogId}|${alert.issueType}|${alert.issue}`}
          to={`/customers/${alert.customerId}`}
          className="ferro-hero__alert"
        >
          <span className="ferro-hero__alert-dog">{alert.dogName}</span>
          <span className="ferro-hero__alert-issue">{alert.issue}</span>
        </Link>
      ))}
      {overflow > 0 ? (
        <Link to="/compliance" className="ferro-hero__alert-more">
          {t('admin.dashboard.hero.alertsMore', { count: overflow })}
        </Link>
      ) : null}
    </div>
  );
}
